import React, { useEffect, useRef } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { MenuDotsSvg, LocationSvg, DocumentSvg } from "components/icons";
import { SecondaryTextSmBold, SecondaryTextSm, ProgressBar, Tooltip } from "components/atoms";
import styled, { css } from "styled-components";
import { formatDistance, differenceInSeconds, differenceInDays } from 'date-fns';
import { CompletionTime } from '../atoms'
import { today } from 'AppReact';
import { getEmptyImage } from 'react-dnd-html5-backend';

const Row = styled.div`
  ${({ isDragging, theme }) => css`
    display: flex;
    align-items: center;
    border-bottom: 1px solid #e3e3e3;
    height: 70px;
    opacity: ${isDragging ? .5 : 1};

    background-color: ${isDragging ? `${theme?.colour?.primary ?? '#1DBD6B'}26` : ''};

    &:hover {
      background-color: #5A4ED10D; 
    }
  `}
`;

export const SwitchField = ({ assignment, move, index, save, onClick, dragging, setDragging, techName }) => {
  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'assignment',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      move(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop() {
      save();
    }
  });



  const [{ isDragging }, drag, preview] = useDrag({
    type: 'assignment',
    item: () => {
      return { name, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));

  useEffect(() => {
    setDragging(isDragging);
  }, [isDragging]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <Row ref={ref} data-handler-id={handlerId} isDragging={isDragging} onClick={() => onClick?.()}>
      <div className="flex items-center" style={{ width: '33%' }}>
        <div className="cursor-move mr-2 ml-2">
          {dragging ?
            <MenuDotsSvg multiple /> :
            <Tooltip direction="bottom" offset="start" delay={100} content={
              <div className="whitespace-nowrap">
                <div>{`Drag and drop to change the default order of ${techName?.split(' ')?.[0] ?? ''}'s work orders`}</div>
              </div>
            }>
              <MenuDotsSvg multiple />
            </Tooltip>
          }
        </div>
        <div className="mr-2">
          <DocumentSvg color={'#5A4ED1'} />
        </div>
        <SecondaryTextSmBold>{assignment.category ?? ''}</SecondaryTextSmBold>
      </div>
      <div className="flex items-center" style={{ width: '33%' }}>
        <div className="mr-2">
          {assignment?.property_icon ? <img src={assignment.property_icon} alt="property_image" width="12px" height="12px" /> : <LocationSvg width="11.33" height="13.33" color="#5A4ED1" />}
        </div>
        <div className="mr-2">
          <SecondaryTextSm>{assignment.property}</SecondaryTextSm>
        </div>
        <SecondaryTextSmBold>{assignment.unit ?? ''}</SecondaryTextSmBold>
      </div>
      <div style={{ width: '33%' }}>
        <div className="flex items-center justify-between">
          <CompletionTime displayState={{ color: '#9EACB0' }}>{`${formatDistance(0, differenceInSeconds(new Date(assignment.assigned_at ?? today()), today()) * 1000, { includeSeconds: true })} ago`}</CompletionTime>
          <CompletionTime displayState={{ color: '#5A4ED1' }}>{`${formatDistance(0, differenceInSeconds(new Date(assignment.assigned_at ?? today()), today()) * 1000, { includeSeconds: true })} ago`}</CompletionTime>
        </div>
        <div>
          <ProgressBar
            progress={(differenceInDays(assignment.assigned_at, new Date()) / differenceInDays(new Date(assignment.assigned_at), new Date())) * 100}
            reversed
            secondColor='#9EACB0'
            color='#5A4ED1'
            height={8}
          />
        </div>
      </div>
    </Row>
  )
}